import styled, { css } from "styled-components/macro";
import { useIsDarkMode } from "store/atoms";
import {
  FLIGHT_CONFIG_DESCRIPTIONS,
  FlightConfigType,
  useFlightConfig,
  useIsFlightConfigInitialized,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import { useEffect, useState } from "react";
import { Collapse, IconButton } from "@mui/material";
import {
  Check,
  ChevronRight,
  Close,
  Settings,
  OpenInNew,
} from "@mui/icons-material";
import { flDanger60, flSuccess60 } from "constants/colors";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useLocalStorage } from "react-use";
import { FlagsEditorStyles } from "./FlagsEditor";
import { CustomJsonEditor } from "./CustomJsonEditor";
import { blueCheckbox } from "reports/constants/colors";

const EDITABLE_CONFIG_KEYS: (keyof FlightConfigType)[] = [
  "general",
  "metrics",
  "trendChart",
  "reportConfig",
  "hubletView",
];

export function FlightConfigEditor() {
  const [isFlightConfigInitialized] = useIsFlightConfigInitialized();
  const [, , flightConfigRaw] = useFlightConfig();
  const [isDarkMode] = useIsDarkMode();
  const [editedJson, setEditedJson] = useState({
    ...flightConfigRaw,
  });
  useEffect(() => {
    setEditedJson(flightConfigRaw);
  }, [flightConfigRaw]);

  const ckey = useCustomerKey();

  const [shouldUseDummy, setShouldUseDummy] = useState(false);
  return (
    <EditorStyles $isDarkMode={isDarkMode}>
      <FlagsEditorStyles $isDarkMode={isDarkMode} style={{ height: "100%" }}>
        <h2 className="header">
          Flight Config
          <div className="links">
            <a
              href={`https://portal.raven.ai/${ckey}/flightConfig`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Flight Config Editor
              <Settings /> <OpenInNew />
            </a>
          </div>
        </h2>
        {/* can validate with 'ajv' + jsonschema https://www.npmjs.com/package/jsoneditor-react */}
        {isFlightConfigInitialized === "unknown" ? (
          "Fetching flight config..."
        ) : isFlightConfigInitialized === "uninitialized" && !shouldUseDummy ? (
          <div>
            No flight config found. Please{" "}
            <a
              href={`https://${ckey}.gke-us-east1.raven-cloud.com/midgard/admin/deployment_config/flightconfig/add/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              add a flight config to the database
            </a>{" "}
            or{" "}
            <button onClick={() => setShouldUseDummy(true)}>
              use a dummy config just to experiment
            </button>
            .
          </div>
        ) : (
          <ErrorBoundary id="FlightConfigEditor">
            {EDITABLE_CONFIG_KEYS.map(key => {
              return (
                <ConfigEditorSection
                  key={key}
                  flightConfigKey={key}
                  setEditedJson={setEditedJson}
                  editedJson={editedJson}
                />
              );
            })}
          </ErrorBoundary>
        )}
      </FlagsEditorStyles>
    </EditorStyles>
  );
}

const EditorStyles = styled.div<{ $isDarkMode: boolean }>`
  height: 100%;
  .header {
    position: sticky;
    z-index: 1;
    margin-top: -28px;
    top: -28px;
    display: flex;
    gap: 24px;
    pointer-events: none;
  }
  .links {
    pointer-events: auto;
    display: flex;
    flex-grow: 1;
    justify-content: end;
    gap: 12px;
    margin-right: -24px;
    a {
      display: flex;
      align-items: center;
      gap: 0.25em;
      svg {
        margin-right: 0;
      }
    }
  }
  .submit-buttons {
    display: flex;
    gap: 12px;
    margin-top: 6px;
    margin-left: auto;
    width: fit-content;
  }
  .btnSubmit {
  }
  .btnReset {
    opacity: 0.5;
  }
  ${({ $isDarkMode }) =>
    $isDarkMode
      ? css`
          .btnSubmit:not(:disabled) {
            color: ${flSuccess60};
          }
          .btnReset:not(:disabled) {
            color: ${flDanger60};
          }
        `
      : css`
          .btnSubmit:not(:disabled) {
            color: #558d4f;
          }
          .btnReset:not(:disabled) {
            color: #9a2323;
          }
        `}
`;

const UnsavedChangesNotification = ({ onReset, onSubmit }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "8px",
        right: "0",
        zIndex: 3,
        color: blueCheckbox,
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      🙋‍♂️ unsaved changes - click
      <IconButton
        size="small"
        style={{ padding: "4px", color: "tomato" }}
        onClick={onReset}
      >
        <Close fontSize="small" />
      </IconButton>
      or
      <IconButton
        size="small"
        style={{ padding: "4px", color: "forestgreen" }}
        onClick={onSubmit}
      >
        <Check fontSize="small" />
      </IconButton>
    </div>
  );
};

function ConfigEditorSection({ flightConfigKey, editedJson, setEditedJson }) {
  const [flightConfig, setFlightConfig, flightConfigRaw] = useFlightConfig();

  const [collapsedLs, setCollapsed] = useLocalStorage(
    `debugger:flightConfig:collapsed:${flightConfigKey}`,
    false
  );
  const collapsed = collapsedLs ?? false;
  const flightConfigValue = {
    ...flightConfig[flightConfigKey],
    ...flightConfigRaw[flightConfigKey],
  };

  const ordered: Record<string, any> = Object.keys(flightConfigValue)
    .sort()
    .reduce((obj, key) => {
      obj[key] = flightConfigValue[key];
      return obj;
    }, {});

  const [edited, setEdited] = useState(false);
  // JsonEditor doesn't re-render when the value changes, so we need to force it to re-render
  const [rerenderKey, setRerenderKey] = useState(0);
  // Functions for reset and submit
  const handleReset = () => {
    setEdited(false);
    setEditedJson(flightConfigRaw);
    setRerenderKey(prev => prev + 1);
  };

  const handleSubmit = () => {
    setEdited(false);
    setFlightConfig(editedJson);
    setRerenderKey(prev => prev + 1);
  };

  return (
    <ConfigEditorSectionStyles $edited={edited}>
      {edited && (
        <UnsavedChangesNotification
          onReset={handleReset}
          onSubmit={handleSubmit}
        />
      )}
      <h2 onClick={() => setCollapsed(!collapsed)}>
        <ChevronRight
          style={{
            transform: `rotate(${collapsed ? 0 : 0.25}turn)`,
          }}
        />{" "}
        {flightConfigKey}
      </h2>
      <Collapse unmountOnExit={true} in={!collapsed}>
        <div
          style={{
            marginBottom: 6,
          }}
        >
          {FLIGHT_CONFIG_DESCRIPTIONS[flightConfigKey]}
        </div>
        <CustomJsonEditor
          key={rerenderKey}
          value={ordered}
          onChange={newValue => {
            setEdited(true);
            setEditedJson(prev => ({ ...prev, [flightConfigKey]: newValue }));
          }}
        />
        <div className="submit-buttons">
          <IconButton
            className="btnReset"
            disabled={!edited}
            onClick={handleReset}
          >
            <Close />
          </IconButton>
          <IconButton
            className="btnSubmit"
            disabled={!edited}
            onClick={handleSubmit}
          >
            <Check />
          </IconButton>
        </div>
      </Collapse>
    </ConfigEditorSectionStyles>
  );
}
const ConfigEditorSectionStyles = styled.div<{ $edited: boolean }>`
  ${props =>
    props.$edited &&
    css`
      .jsoneditor-text {
        background: #f5f5d7;
      }
      .MuiIconButton-root {
        background: rgba(0, 0, 0, 0.08);
      }
      position: relative;
      &:after {
        content: "";
        position: absolute;
        top: -12px;
        left: -12px;
        right: -12px;
        bottom: -12px;
        border: 2px dashed ${blueCheckbox};
        border-radius: 10px;
        z-index: 3;
        pointer-events: none;
      }
    `}
`;
